import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from './ui/Modal'
import { Input, Select } from './ui/Input'
import { Button } from './ui/Button'
import { useAuthStore } from '../store/auth'

interface CreateOrganizationModalProps {
  isOpen: boolean
  onClose: () => void
}

const organizationTypes = [
  { value: 'association', label: 'Yhdistys' },
  { value: 'company', label: 'Yritys' },
  { value: 'foundation', label: 'Säätiö' },
  { value: 'other', label: 'Muu' },
]

export default function CreateOrganizationModal({ isOpen, onClose }: CreateOrganizationModalProps) {
  const { t } = useTranslation()
  const { createOrganization } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    businessId: '',
    type: 'association',
    vatRegistered: false,
    fiscalYearStart: '01-01',
  })

  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const validate = () => {
    const errors: Record<string, string> = {}
    if (!formData.name.trim()) {
      errors.name = 'Organisaation nimi on pakollinen'
    }
    if (formData.businessId && !/^\d{7}-\d$/.test(formData.businessId)) {
      errors.businessId = 'Y-tunnuksen muoto on virheellinen (esim. 1234567-8)'
    }
    if (!/^\d{2}-\d{2}$/.test(formData.fiscalYearStart)) {
      errors.fiscalYearStart = 'Muodon tulee olla KK-PP (esim. 01-01)'
    }
    return errors
  }

  const errors = validate()
  const isValid = Object.keys(errors).length === 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ name: true, businessId: true, fiscalYearStart: true })

    if (!isValid) return

    setIsSubmitting(true)
    setError(null)

    try {
      await createOrganization({
        name: formData.name.trim(),
        businessId: formData.businessId || undefined,
        type: formData.type,
        vatRegistered: formData.vatRegistered,
        fiscalYearStart: formData.fiscalYearStart,
      })

      setSuccess(true)
      // Reset form
      setFormData({
        name: '',
        businessId: '',
        type: 'association',
        vatRegistered: false,
        fiscalYearStart: '01-01',
      })
      setTouched({})
      
      // Close after short delay
      setTimeout(() => {
        setSuccess(false)
        onClose()
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Organisaation luonti epäonnistui')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null)
      setSuccess(false)
      setFormData({
        name: '',
        businessId: '',
        type: 'association',
        vatRegistered: false,
        fiscalYearStart: '01-01',
      })
      setTouched({})
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <Modal title={t('org.createTitle')} onClose={handleClose} size="medium">
      {success ? (
        <div className="text-center py-8">
          <div className="text-5xl mb-4">✅</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Organisaatio luotu!</h3>
          <p className="text-gray-600">Sinut on ohjattu uuteen organisaatioon.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <Input
            label="Organisaation nimi *"
            placeholder="esim. Kotikirjuri Ry"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            onBlur={() => setTouched({ ...touched, name: true })}
            error={touched.name ? errors.name : undefined}
            autoFocus
          />

          <Input
            label="Y-tunnus"
            placeholder="1234567-8"
            value={formData.businessId}
            onChange={(e) => setFormData({ ...formData, businessId: e.target.value })}
            onBlur={() => setTouched({ ...touched, businessId: true })}
            error={touched.businessId ? errors.businessId : undefined}
            helperText="Valinnainen. Muoto: 1234567-8"
          />

          <Select
            label="Organisaation tyyppi"
            options={organizationTypes}
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          />

          <Input
            label="Tilikauden aloituspäivä"
            placeholder="01-01"
            value={formData.fiscalYearStart}
            onChange={(e) => setFormData({ ...formData, fiscalYearStart: e.target.value })}
            onBlur={() => setTouched({ ...touched, fiscalYearStart: true })}
            error={touched.fiscalYearStart ? errors.fiscalYearStart : undefined}
            helperText="Muoto: KK-PP (esim. 01-01 kalenterivuodelle)"
          />

          <div className="flex items-center gap-3 py-2">
            <input
              type="checkbox"
              id="vatRegistered"
              checked={formData.vatRegistered}
              onChange={(e) => setFormData({ ...formData, vatRegistered: e.target.checked })}
              className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="vatRegistered" className="text-sm text-gray-700">
              Organisaatio on arvonlisäverovelvollinen
            </label>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Peruuta
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={!isValid || isSubmitting}
              className="flex-1"
            >
              Luo organisaatio
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            * Pakollinen kenttä
          </p>
        </form>
      )}
    </Modal>
  )
}
